'use client';

import React, {
  MouseEventHandler,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import { useLocale } from 'next-intl';

import { AccentFragment } from '@/blocks/components/HeadingAccentFragment';
import { ChevronLeft, ChevronRight } from '@/blocks/components/Icons/Chevron';
import { CardsGrid } from '@/blocks/components/layout/CardsGrid';
import { siteVersion } from '@/global/public-settings';
import { useIsRtl } from '@/hooks/useIsRtl';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';

import { SocialLink } from '@/app/[lang]/community/sections/SocialLinksSection/SocialLink';
import {
  useSocialLinksAfricaData,
  useSocialLinksData,
} from '@/app/[lang]/community/sections/SocialLinksSection/useSocialLinksData';

import styles from './styles.module.scss';

function ArrowButton({
  visible,
  onClick,
  children,
}: PropsWithChildren<{ visible: boolean; onClick: MouseEventHandler }>) {
  return (
    <button className={classNames(styles.arrowBtn, !visible && styles.arrowHidden)} onClick={onClick}>
      <AccentFragment>{children}</AccentFragment>
    </button>
  );
}

export function SocialTabsContent({ lang }: { lang: string }) {
  const socialLinksData = useSocialLinksData();
  const socialLinksAfricaData = useSocialLinksAfricaData();

  const socialLinks = siteVersion.isAfrica ? socialLinksAfricaData : socialLinksData;

  const locale = useLocale();

  const [activeTab, setActiveTab] = useState<string>(locale in socialLinks ? locale : 'en');
  const [scrollStartVisible, setScrollStartVisible] = useState(false);
  const [scrollEndVisible, setScrollEndVisible] = useState(false);
  const { isRtl } = useIsRtl();

  const activeTabRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);

  const commonScrollProps: ScrollIntoViewOptions = useMemo(
    () => ({
      behavior: 'smooth',
      block: 'nearest',
    }),
    [],
  );

  const getButtons = useCallback(
    () => Array.from(buttonsContainerRef?.current?.querySelectorAll('button') || []),
    [],
  );

  // scroll to active tab
  useEffect(() => {
    if (!activeTabRef.current || !containerRef.current) {
      return;
    }

    const activeTabCenter =
      activeTabRef?.current?.getBoundingClientRect()?.right -
      activeTabRef?.current?.getBoundingClientRect()?.width / 2;
    const viewPortCenter = window.innerWidth / 2;
    const scrollPosition = activeTabCenter - viewPortCenter + containerRef?.current?.scrollLeft;

    containerRef?.current?.scrollTo({ left: scrollPosition, ...commonScrollProps });
  }, [activeTab, commonScrollProps, isRtl]);

  // on scroll to end - last visible button moves from end to beginning [123(4)]5678 => 123[(4)567]8
  const scrollEnd = useCallback(
    () =>
      getButtons()
        .findLast((btn) =>
          isRtl
            ? btn?.getBoundingClientRect()?.right >
              (containerRef?.current?.getBoundingClientRect()?.left || 0)
            : btn?.getBoundingClientRect()?.left <
              (containerRef?.current?.getBoundingClientRect()?.right || 0),
        )
        ?.scrollIntoView({ inline: 'start', ...commonScrollProps }),
    [],
  );

  // on scroll to start - first visible button moves from beginning to end 123[(4)567]8 => [123(4)]5678
  const scrollStart = useCallback(
    () =>
      getButtons()
        .find((btn) =>
          isRtl
            ? btn?.getBoundingClientRect()?.left <
              (containerRef?.current?.getBoundingClientRect()?.right || 0)
            : btn?.getBoundingClientRect()?.right >
              (containerRef?.current?.getBoundingClientRect()?.left || 0),
        )
        ?.scrollIntoView({ inline: 'end', ...commonScrollProps }),
    [],
  );

  // show/hide arrows
  useEffect(() => {
    const container = containerRef.current;
    const buttonsContainer = buttonsContainerRef.current;
    const scrollToStartBtn = getButtons().at(0);
    const scrollToEndBtn = getButtons().at(-1);

    if (!container || !buttonsContainer || !scrollToStartBtn || !scrollToEndBtn) {
      return;
    }

    const obs = new IntersectionObserver(
      (args) => {
        args.forEach((entry) => {
          if (entry.target === scrollToStartBtn) {
            setScrollStartVisible(!entry.isIntersecting);
          }
          if (entry.target === scrollToEndBtn) {
            setScrollEndVisible(!entry.isIntersecting);
          }
        });
      },
      {
        root: container,
        rootMargin: '10px',
        threshold: 1,
      },
    );

    obs.observe(scrollToStartBtn);
    obs.observe(scrollToEndBtn);

    return () => obs.disconnect();
  }, []);

  // Hide turkish community on main site
  const tabs = useMemo(
    () => Object.entries(socialLinks).filter(([lang]) => (siteVersion.isTurkey ? true : lang !== 'tr')),
    [socialLinks],
  );

  return (
    <div>
      <div className={styles.wrapperTabsContent}>
        <div className={classNames(styles.tabButtons, siteVersion.isAfrica && styles.justifyCenter)}>
          <ArrowButton onClick={scrollStart} visible={scrollStartVisible}>
            <ChevronLeft />
          </ArrowButton>
          <div className={styles.wrapperGroupBtnsLine} ref={containerRef}>
            <div className={classNames(styles.wrapperBtn, typography.bodyM)} ref={buttonsContainerRef}>
              {tabs.map(([lang, tab]) => (
                <button
                  key={lang}
                  className={classNames(buttons.btnPill, lang === activeTab && buttons.btnPillActive)}
                  onClick={() => setActiveTab(lang)}
                  ref={lang === activeTab ? activeTabRef : undefined}
                >
                  {tab.title}
                </button>
              ))}
            </div>
          </div>
          <ArrowButton onClick={scrollEnd} visible={scrollEndVisible}>
            <ChevronRight />
          </ArrowButton>
        </div>
      </div>
      <CardsGrid columns={{ md: 2, lg: 3 }}>
        {socialLinks[activeTab]?.content.map((el, key) => (
          <SocialLink link={el.link} icon={el.icon} linkTitle={el.title} key={key} className={el.className} />
        ))}
      </CardsGrid>
    </div>
  );
}
